import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useDialogStore } from '@/stores/dialog'
import { isToday } from '@/utils/date'
import { showLoadingToast, closeToast } from '@/utils/toast'

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/home/index.vue'),
    meta: {
      title: 'home',
      keepAlive: true,
      isTabPage: true, // 标识为底部标签页
      requiresAuth: true
    }
  },
  {
    path: '/message',
    name: 'Message',
    component: () => import('@/views/message/index.vue'),
    meta: {
      title: 'message',
      isTabPage: true, // 标识为底部标签页
      requiresAuth: true
    }
  },
  {
    path: '/chat',
    name: 'Chat',
    component: () => import('@/views/message/chat.vue'),
    meta: {
      title: 'chat',
      requiresAuth: true,
      hideTabbar: true
    }
  },
  {
    path: '/detail',
    name: 'Detail',
    component: () => import('@/views/message/detail.vue'),
    meta: {
      title: 'detail',
      requiresAuth: true,
      hideTabbar: true
    }
  },
  {
    path: '/store',
    name: 'Store',
    component: () => import('@/views/store/index.vue'),
    meta: {
      title: 'store',
      isTabPage: true, // 标识为底部标签页
      requiresAuth: true
    }
  },
  {
    path: '/mine',
    name: 'mine',
    component: () => import('@/views/mine/index.vue'),
    meta: {
      title: 'mine',
      isTabPage: true, // 标识为底部标签页
      requiresAuth: true
    }
  },
  {
    path: '/edit-profile',
    name: 'EditProfile',
    component: () => import('@/views/mine/Profile.vue'),
    meta: {
      title: 'edit-profile',
      requiresAuth: true,
      hideTabbar: true // 标识为底部标签页
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/login.vue'),
    meta: {
      title: 'login',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/faq',
    name: 'FAQ',
    component: () => import('@/views/fqa/index.vue'),
    meta: {
      title: 'fqa',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/policy',
    name: 'Policy',
    component: () => import('@/views/policy/index.vue'),
    meta: {
      title: 'policy',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/terms/index.vue'),
    meta: {
      title: 'terms',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/like',
    name: 'Like',
    component: () => import('@/views/mine/like.vue'),
    meta: {
      title: 'like',
      hideTabbar: true,
      requiresAuth: true
    }
  },

  {
    path: '/check-in',
    name: 'CheckInCenter',
    component: () => import('@/views/checkInCenter/index.vue'),
    meta: {
      title: 'check-in',
      hideTabbar: true,
      requiresAuth: true
    }
  },

  {
    path: '/search',
    name: 'Search',
    component: () => import('@/views/search/index.vue'),
    meta: {
      title: 'search',
      keepAlive: false,
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/404.vue'),
    meta: {
      title: '404',
      hideTabbar: true,
      requiresAuth: false
    }
  },
  {
    path: '/task',
    name: 'Task',
    component: () => import('@/views/mine/task.vue'),
    meta: {
      title: 'task',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/setting',
    name: 'Setting',
    component: () => import('@/views/setting/index.vue'),
    meta: {
      title: 'Setting',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/backpack',
    name: 'Backpack',
    component: () => import('@/views/mine/backpack.vue'),
    meta: {
      title: 'backpack',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/account',
    name: 'Account',
    component: () => import('@/views/account/index.vue'),
    meta: {
      title: 'account',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/vip',
    name: 'Vip',
    component: () => import('@/views/vip/index.vue'),
    meta: {
      title: 'vip',
      hideTabbar: true,
      requiresAuth: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    // 对 keepAlive 或底部 Tab 页，保持当前位置（不强制滚到顶部）
    if (to.meta?.keepAlive || to.meta?.isTabPage) return false
    return { top: 0 }
  }
})

// 全局路由加载计时器（用于控制延迟显示 loading）
let routeLoadingTimer: ReturnType<typeof setTimeout> | null = null
// 3秒埋点计时器
let slowLoadingTimer: ReturnType<typeof setTimeout> | null = null
// 记录导航开始时间
let navigationStartTime: number = 0

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 初始化用户状态
  const userStore = useUserStore()
  // const appStore = useAppStore()
  const dialogStore = useDialogStore()
  const isNewUser = localStorage.getItem('isNewUserLogin')
  let token = userStore.getStoredToken()

  // 🔧 Chat 页面加载监控
  if (to.name === 'Chat') {
    // 清理之前的定时器
    if (routeLoadingTimer) {
      clearTimeout(routeLoadingTimer)
      routeLoadingTimer = null
    }
    if (slowLoadingTimer) {
      clearTimeout(slowLoadingTimer)
      slowLoadingTimer = null
    }

    // 记录导航开始时间
    navigationStartTime = Date.now()

    // 1秒后显示 loading
    routeLoadingTimer = setTimeout(() => {
      showLoadingToast('')
    }, 1000)

    // 3秒后记录埋点
    slowLoadingTimer = setTimeout(() => {
      const loadingTime = Date.now() - navigationStartTime
      import('@/plugins/firebase').then(({ logEventSafe }) => {
        logEventSafe('chat_page_slow_loading', {
          duration: loadingTime,
          from: from.path,
          to: to.path,
          network: (navigator as any).connection?.effectiveType || 'unknown'
        })
      })
      console.warn('⚠️ Chat 页面加载超过 3 秒:', loadingTime, 'ms')
    }, 4000)
  }

  // 优化：首页优先加载，不阻塞路由跳转
  // 如果是首页，立即通过，让用户看到界面
  if (to.name === 'Home') {
    // 如果没有token，后台异步初始化认证，不阻塞路由
    if (!token) {
      userStore.initializeAuth().catch((e) => {
        console.error('Auth initialization failed:', e)
        import('@/plugins/firebase').then(({ logEventSafe }) => {
          logEventSafe('page_loading_failed', {
            error: e instanceof Error ? e.message : 'Auth initialization failed',
            path: to.path,
            reason: 'auth_init_error'
          })
        })
      })
    }

    // 延迟显示新用户弹窗，不阻塞路由
    setTimeout(() => {
      if (
        isNewUser !== '1' &&
        isToday(userStore.getTouristInfo()?.createdAt) &&
        userStore.getMsgCost() > 0
      ) {
        dialogStore.openAppNewUser()
      }
      localStorage.setItem('isNewUserLogin', '1')
    }, 2000)

    // 立即通过路由，不等待认证
    // appStore.setLoading(false)
    next()
    return
  }

  // 其他页面：如果token不存在，则初始化用户状态（但尽量不阻塞）
  try {
    if (!token) {
      // 对于非首页，可以等待认证，但设置超时避免长时间阻塞
      await Promise.race([
        userStore.initializeAuth(),
        new Promise(resolve => setTimeout(resolve, 2000)) // 2秒超时
      ])
    }

    setTimeout(() => {
      if (
        isNewUser !== '1' &&
        isToday(userStore.getTouristInfo()?.createdAt) &&
        userStore.getMsgCost() > 0
      ) {
        dialogStore.openAppNewUser()
      }
      localStorage.setItem('isNewUserLogin', '1')
    }, 2000)
  } catch (e) {
    console.error('Auth initialization failed:', e)
    // 页面加载失败埋点
    import('@/plugins/firebase').then(({ logEventSafe }) => {
      logEventSafe('page_loading_failed', {
        error: e instanceof Error ? e.message : 'Auth initialization failed',
        path: to.path,
        reason: 'auth_init_error'
      })
    })
  }

  // 显示加载状态
  // appStore.setLoading(true)

  // 设置页面标题
  if (to.meta?.title) {
    // 根据路由名称设置更友好的标题
    const titleMap: Record<string, string> = {
      login: 'Login',
      home: 'Home',
      mine: 'Profile',
      store: 'Store',
      message: 'Messages',
      like: 'Favorites',
      fqa: 'FAQ',
      policy: 'Privacy Policy',
      terms: 'Terms of Service',
      EditProfile: 'Edit Profile',
      '404': 'Page Not Found'
    }

    const pageTitle = titleMap[to.meta.title as string] || to.meta.title
    document.title = `${pageTitle} - Roleplay H5`
  } else {
    // 如果没有设置标题，使用默认标题
    document.title = 'Roleplay H5'
  }

  // 如果是登录页面，直接通过，避免循环检查
  if (to.name === 'Login') {
    next()
    return
  }

  // 检查是否需要登录（优先使用token判断）
  // if (to.meta?.requiresAuth) {
  //   let token = userStore.getStoredToken()

  //   // 若无token，直接跳转登录页面
  //   if (!token) {
  //     console.log('无token，跳转登录页面', { to: to.name, from: from.name })

  //     // 确保立即结束加载状态
  //     appStore.setLoading(false)

  //     next({
  //       name: 'Login',
  //       query: { redirect: to.fullPath }
  //     })
  //     return
  //   }
  // }

  // 如果已登录，访问登录页面则重定向到首页
  if (userStore.isLoggedIn && ['Login', 'Register'].includes(to.name as string)) {
    next({ name: 'Home' })
    return
  }

  next()
})

router.afterEach(async (to, from) => {
  // 🔧 清理 Chat 页面加载监控的定时器
  if (routeLoadingTimer) {
    clearTimeout(routeLoadingTimer)
    routeLoadingTimer = null
  }
  if (slowLoadingTimer) {
    clearTimeout(slowLoadingTimer)
    slowLoadingTimer = null
  }

  // 关闭 loading toast
  import('@/utils/toast').then(({ closeToast }) => {
    closeToast()
  })

  // 🔧 如果成功导航到 Chat 页面，记录加载时间
  if (to.name === 'Chat' && navigationStartTime > 0) {
    const loadingTime = Date.now() - navigationStartTime
    console.log('✅ Chat 页面加载完成:', loadingTime, 'ms')

    // 记录正常加载时间埋点
    import('@/plugins/firebase').then(({ logEventSafe }) => {
      logEventSafe('chat_page_loaded', {
        duration: loadingTime,
        from: from.path,
        network: (navigator as any).connection?.effectiveType || 'unknown'
      })
    })

    navigationStartTime = 0
  }



  // 页面访问统计
  if (import.meta.env.PROD) {
    // 这里可以添加页面访问统计代码
    console.log(`Page visited: ${to.path}`)
  }

  // 如果是退出登录后的跳转，记录日志
  if (to.name === 'Login' && from.name === 'mine') {
    console.log('退出登录完成，已跳转到登录页面')
  }
})

// 路由错误处理
router.onError(error => {
  console.error('Router error:', error)

  // 发生路由错误时，确保关闭 loading
  if (routeLoadingTimer) {
    clearTimeout(routeLoadingTimer)
    routeLoadingTimer = null
  }
  closeToast()

  // 页面加载失败埋点
  import('@/plugins/firebase').then(({ logEventSafe }) => {
    logEventSafe('page_loading_failed', {
      error: error?.message || 'Unknown router error',
      stack: error?.stack || 'No stack trace',
      path: window.location.pathname
    })
  })

  // 可以在这里添加错误上报
  if (import.meta.env.PROD) {
    // 错误上报逻辑
  }
})

export default router
